import styles from './NewTable.module.scss';
import {Checkbox, IconButton, TableCell} from '@mui/material';
import {AddOutlined, DeleteOutlined} from '@mui/icons-material';
import {RowEditType, TableHeadProps} from './types';
import {ButtonWithConfirm} from 'components/ButtonWithConfirm/ButtonWithConfirm';
import cn from 'classnames';
import {SettingsMenu} from '@ui/SettingsMenu/SettingsMenu';
import {useTranslation} from 'react-i18next';
import i18n from 'i18n';

type TableHeadActionsProps = Omit<TableHeadProps, 'columns' | 'setPage'> &
    Pick<TableHeadProps, 'cellClassName' | 'hideBottomBorder'>;

export const TableHeadActions = ({
    tableConfig,
    disableAction,
    onSelectAllClick,
    onAdd,
    onDelete,
    onDeleteClick,
    confirmDeletingDescription,
    selected = [],
    rowCount = 0,
    totalElements = 0,
    cellClassName,
    renderAdditionalHeaderActions,
    hideBottomBorder,
}: TableHeadActionsProps) => {
    const {
        canDelete,
        canCreate,
        elementsMaxLimit,
        withOrders,
        canEdit,
        editType = RowEditType.INLINE,
        emptyCell = false,
    } = tableConfig;

    const haveDelete = canDelete && editType === RowEditType.INLINE;

    const {t} = useTranslation('common');

    return (
        <>
            {(canDelete || canCreate || canEdit || emptyCell) && (
                <TableCell
                    padding='none'
                    className={cn(styles.sticky, cellClassName, {
                        [styles.stickyNoBorder]: hideBottomBorder,
                    })}
                    style={{zIndex: 4, maxWidth: '130px'}}
                >
                    {editType === RowEditType.POPUP && canEdit && (
                        <span style={{width: '35px', display: 'inline-block'}} />
                    )}
                    {editType === RowEditType.POPUP && canDelete && (
                        <span style={{width: '35px', display: 'inline-block'}} />
                    )}
                    {emptyCell && <span style={{width: '35px', display: 'inline-block'}} />}
                    {withOrders && <span style={{width: '25px', display: 'inline-block'}} />}
                    {haveDelete && (
                        <span>
                            <Checkbox
                                disabled={disableAction || rowCount === 0}
                                color='primary'
                                indeterminate={selected.length > 0 && selected.length < rowCount}
                                checked={rowCount > 0 && selected.length === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{
                                    'aria-label': 'select all desserts',
                                }}
                            />
                        </span>
                    )}
                    {canCreate && (
                        <span>
                            <IconButton
                                onClick={() => {
                                    onAdd && onAdd();
                                }}
                                disabled={
                                    (elementsMaxLimit
                                        ? totalElements >= elementsMaxLimit
                                        : false) || disableAction
                                }
                                color='inherit'
                            >
                                <AddOutlined />
                            </IconButton>
                        </span>
                    )}
                    {haveDelete && !renderAdditionalHeaderActions && (
                        <span>
                            <ButtonWithConfirm
                                fontSize='medium'
                                onConfirm={() => onDelete && onDelete(selected)}
                                onClick={async () => {
                                    if (onDeleteClick) {
                                        await onDeleteClick(selected.map((i) => String(i)));
                                    }
                                }}
                                title={i18n.t('useConfirmDeletingDescription.sureDeleteRecords', {
                                    ns: 'supervising',
                                })}
                                disabled={selected.length === 0 || disableAction}
                                description={confirmDeletingDescription}
                            />
                        </span>
                    )}
                    {renderAdditionalHeaderActions && (
                        <SettingsMenu
                            disabled={disableAction}
                            items={[
                                ...renderAdditionalHeaderActions({selected}),
                                ...(haveDelete
                                    ? [
                                          {
                                              trigger: t('delete'),
                                              handler: () => {
                                                  onDelete?.(selected);
                                              },
                                              disabled: selected.length === 0,
                                              icon: <DeleteOutlined color='action' />,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    )}
                </TableCell>
            )}
        </>
    );
};
